import * as THREE from 'three';
import { ROOM } from './world';

const PLAYER_RADIUS = 0.35;
const PLAYER_HEIGHT = 1.6;
const WALK_SPEED = 3.2;
const LOOK_SENSITIVITY = 0.0022;
const TOUCH_LOOK_SENSITIVITY = 0.005;
const PITCH_LIMIT = Math.PI / 2 - 0.08;

/**
 * 一人称コントロール。
 * PC: PointerLock + WASD/矢印キー / スマホ: input.move（バーチャルスティック値）+ 画面ドラッグ視点
 *
 * @param {THREE.PerspectiveCamera} camera
 * @param {HTMLElement} domElement  canvas
 * @param {THREE.Box3[]} colliders  壁・家具のAABB
 * @param {{move: {x: number, y: number}}} input  Reactスティックが書き込む移動ベクトル(-1..1)
 * @param {(locked: boolean) => void} onLockChange
 */
export function createControls(camera, domElement, colliders, input, onLockChange) {
  let yaw = 0; // yaw=0 は -z 方向 = 入口(z+)からカウンター(z-)を向いてスタート
  let pitch = 0;
  let bobPhase = 0;
  let bobAmount = 0;
  const keys = new Set();
  const position = new THREE.Vector3(0, PLAYER_HEIGHT, 3.4);

  camera.position.copy(position);
  camera.rotation.order = 'YXZ';

  // ── PC: PointerLock ──
  const requestLock = () => {
    if (domElement.requestPointerLock) domElement.requestPointerLock();
  };
  const isLocked = () => document.pointerLockElement === domElement;
  const lockSupported = () =>
    typeof document.pointerLockElement !== 'undefined' && !!domElement.requestPointerLock;
  const handleLockChange = () => {
    keys.clear(); // ロック切替の瞬間にkeyupを取り逃しても押しっぱなしにしない
    onLockChange(isLocked());
  };
  const handleBlur = () => keys.clear();
  const handleVisibility = () => {
    if (document.hidden) keys.clear();
  };
  const handleMouseMove = (e) => {
    if (!isLocked()) return;
    yaw -= e.movementX * LOOK_SENSITIVITY;
    pitch -= e.movementY * LOOK_SENSITIVITY;
    pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
  };
  const handleKeyDown = (e) => keys.add(e.code);
  const handleKeyUp = (e) => keys.delete(e.code);

  // ── スマホ: 画面ドラッグで視点 ──
  let lookTouchId = null;
  let lastTouch = { x: 0, y: 0 };
  const handleTouchStart = (e) => {
    for (const t of e.changedTouches) {
      // 左下スティック領域はReact側が処理するので、それ以外を視点用にする
      if (lookTouchId === null) {
        lookTouchId = t.identifier;
        lastTouch = { x: t.clientX, y: t.clientY };
      }
    }
  };
  const handleTouchMove = (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier === lookTouchId) {
        yaw -= (t.clientX - lastTouch.x) * TOUCH_LOOK_SENSITIVITY;
        pitch -= (t.clientY - lastTouch.y) * TOUCH_LOOK_SENSITIVITY;
        pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
        lastTouch = { x: t.clientX, y: t.clientY };
      }
    }
  };
  const handleTouchEnd = (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier === lookTouchId) lookTouchId = null;
    }
  };

  document.addEventListener('pointerlockchange', handleLockChange);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);
  window.addEventListener('blur', handleBlur);
  document.addEventListener('visibilitychange', handleVisibility);
  domElement.addEventListener('touchstart', handleTouchStart, { passive: true });
  domElement.addEventListener('touchmove', handleTouchMove, { passive: true });
  domElement.addEventListener('touchend', handleTouchEnd, { passive: true });

  // 円柱（XZ円）とAABBの衝突判定
  const collides = (x, z) => {
    const { width: W, depth: D } = ROOM;
    if (x < -W / 2 + PLAYER_RADIUS || x > W / 2 - PLAYER_RADIUS) return true;
    if (z < -D / 2 + PLAYER_RADIUS || z > D / 2 - PLAYER_RADIUS) return true;
    for (const box of colliders) {
      if (box.max.y < 0.3) continue; // 床すれすれの物は跨げる扱い
      const cx = Math.max(box.min.x, Math.min(x, box.max.x));
      const cz = Math.max(box.min.z, Math.min(z, box.max.z));
      const dx = x - cx;
      const dz = z - cz;
      if (dx * dx + dz * dz < PLAYER_RADIUS * PLAYER_RADIUS) return true;
    }
    return false;
  };

  const update = (dt) => {
    // キー入力とスティック入力を合成
    // 視点ロック中以外はキー移動を無効化（ポップアップ表示中などの誤動作防止）
    let mx = input.move.x;
    let mz = input.move.y;
    if (!lockSupported() || isLocked()) {
      if (keys.has('KeyW') || keys.has('ArrowUp')) mz -= 1;
      if (keys.has('KeyS') || keys.has('ArrowDown')) mz += 1;
      if (keys.has('KeyA') || keys.has('ArrowLeft')) mx -= 1;
      if (keys.has('KeyD') || keys.has('ArrowRight')) mx += 1;
    }

    const len = Math.hypot(mx, mz);
    if (len > 1e-3) {
      mx /= Math.max(1, len);
      mz /= Math.max(1, len);
      const sin = Math.sin(yaw);
      const cos = Math.cos(yaw);
      // カメラの向き基準で移動（mz: 前後[-が前], mx: 左右[+が右]）
      const wx = (mx * cos + mz * sin) * WALK_SPEED * dt;
      const wz = (-mx * sin + mz * cos) * WALK_SPEED * dt;
      // 軸ごとに解決して壁ずり移動
      if (!collides(position.x + wx, position.z)) position.x += wx;
      if (!collides(position.x, position.z + wz)) position.z += wz;
      bobPhase += dt * 8.5;
      bobAmount = Math.min(1, bobAmount + dt * 5);
    } else {
      bobAmount = Math.max(0, bobAmount - dt * 5);
    }

    camera.position.copy(position);
    // 歩行時のごく小さな上下ゆれ（酔わない程度）
    camera.position.y += Math.sin(bobPhase) * 0.022 * bobAmount;
    camera.rotation.set(pitch, yaw, 0);
  };

  const dispose = () => {
    document.removeEventListener('pointerlockchange', handleLockChange);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('keyup', handleKeyUp);
    window.removeEventListener('blur', handleBlur);
    document.removeEventListener('visibilitychange', handleVisibility);
    domElement.removeEventListener('touchstart', handleTouchStart);
    domElement.removeEventListener('touchmove', handleTouchMove);
    domElement.removeEventListener('touchend', handleTouchEnd);
    if (isLocked()) document.exitPointerLock();
  };

  return { update, dispose, requestLock, getPosition: () => position, getYaw: () => yaw };
}
